import cx from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, ArrowRefresh2, Marker2, SmileyHappyPlus, UserProfileAdd2 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Text } from 'blaze-ui/Text/Text'
import { Input } from 'almanac-ui/TextInputField/Input'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { EmojiPicker } from 'common/ProseInput/components/EmojiPicker/EmojiPicker'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import Avatar from 'common/Avatar/Avatar'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { BaseEmoji, PostLocation, PostTag, ScheduledPostTarget } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { getInitials } from 'utils'
import { DisabledPickerTrigger } from './DisabledPickerTrigger'
import { LocationPicker } from './LocationPicker'
import { MentionableTextarea } from './MentionableTextarea'
import { MAX_INSTAGRAM_TAGS, MAX_X_TAGS, TagPicker } from './TagPicker'
import Styles from './EditCaptionModal.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

// Use the React-aware native setter so dispatching an `input` event triggers
// React's synthetic onChange/onInput on Input and MentionableTextarea.
const setNativeTextareaValue = (el: HTMLTextAreaElement, value: string) => {
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set
  setter?.call(el, value)
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

const CONNECT_LOCATION_TOOLTIP = 'Connect an account to unlock location'
const CONNECT_TAGGING_TOOLTIP = 'Connect an account to unlock tagging'
const ENABLE_LOCATION_TOOLTIP = 'Enable an account to unlock location'
const ENABLE_TAGGING_TOOLTIP = 'Enable an account to unlock tagging'
const CAROUSEL_TAGGING_TOOLTIP = 'Tagging not available on carousels'
const VIDEO_LOCATION_TOOLTIP = 'Location tagging not supported for videos'

const MAX_TAGS_BY_PROVIDER: Partial<Record<ExternalIntegrationEnum, number>> = {
  [ExternalIntegrationEnum.Instagram]: MAX_INSTAGRAM_TAGS,
  [ExternalIntegrationEnum.X]: MAX_X_TAGS,
}

export const DYNAMIC_CAPTION_PROVIDERS = [
  ExternalIntegrationEnum.Instagram,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.YouTube,
  ExternalIntegrationEnum.TikTok,
  ExternalIntegrationEnum.GoogleBusinessProfile,
] as const

export const LOCATION_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.Instagram,
])

export const TAGS_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.Instagram,
])

export const MENTIONS_SUPPORTED_PROVIDERS: ReadonlySet<ExternalIntegrationEnum> = new Set([
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.Instagram,
])

export type DynamicCaptionProvider = (typeof DYNAMIC_CAPTION_PROVIDERS)[number]

export type DynamicCaptionAccountItem = {
  key: number | string
  target?: ScheduledPostTarget
  accountName: string
  accountAvatar: string | null
  accountType?: 'page' | 'profile' | 'analytics' | 'drive'
  isIntegrationActive?: boolean
}

export type DynamicCaptionRow = {
  provider: DynamicCaptionProvider
  label: string
  targets: ScheduledPostTarget[]
  accountCaptionItems: DynamicCaptionAccountItem[]
  initialCaption: string
  isConnected: boolean
  isEnabled: boolean
  hasAccountIcon: boolean
}

interface CharacterStats {
  count: number
  limit: number
  isOverLimit: boolean
}

interface DynamicCaptionsModalContentProps {
  rows: DynamicCaptionRow[]
  generatingTargets: Partial<Record<number, boolean>>
  updatingProviders: Partial<Record<DynamicCaptionProvider, boolean>>
  isSaving: boolean
  isGeneratingAnyDynamicCaption: boolean
  onToggleProvider: (provider: DynamicCaptionProvider, isChecked: boolean) => void
  onRegenerate: (provider: DynamicCaptionProvider, targetId: number) => void
  onChangeCaption: (targetId: number, value: string) => void
  getDynamicCaptionValue: (target: ScheduledPostTarget) => string
  getCharacterStats: (provider: DynamicCaptionProvider, caption: string) => CharacterStats
  setDynamicCaptionRef: (targetId: number, element: HTMLTextAreaElement | null) => void
  connect: (popup?: Window) => void
  locationsByTargetId: Partial<Record<number, PostLocation | null>>
  onChangeLocation: (targetId: number, location: PostLocation | null) => void
  tagsByTargetId: Partial<Record<number, PostTag[]>>
  onChangeTags: (targetId: number, tags: PostTag[]) => void
  isCarousel: boolean
  isVideo: boolean
}

export const DynamicCaptionsModalContent = ({
  rows,
  generatingTargets,
  updatingProviders,
  isSaving,
  isGeneratingAnyDynamicCaption,
  onToggleProvider,
  onRegenerate,
  onChangeCaption,
  getDynamicCaptionValue,
  getCharacterStats,
  setDynamicCaptionRef,
  connect,
  locationsByTargetId,
  onChangeLocation,
  tagsByTargetId,
  onChangeTags,
  isCarousel,
  isVideo,
}: DynamicCaptionsModalContentProps) => {
  const localTextareaRefs = useRef<Record<number, HTMLTextAreaElement | null>>({})
  const emojiTriggerRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [emojiPickerKey, setEmojiPickerKey] = useState<string | null>(null)
  const [emojiPickerPos, setEmojiPickerPos] = useState<{ bottom: number; left: number } | null>(null)

  // Close the picker on scroll/resize because its position is captured at open
  // time. Re-opening recomputes against the current button rect.
  useEffect(() => {
    if (!emojiPickerKey) return
    const close = () => {
      setEmojiPickerKey(null)
      setEmojiPickerPos(null)
    }
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [emojiPickerKey])

  const handleTextareaRef = (targetId: number, element: HTMLTextAreaElement | null) => {
    localTextareaRefs.current[targetId] = element
    setDynamicCaptionRef(targetId, element)
  }

  const toggleEmojiPicker = (key: string) => {
    if (emojiPickerKey === key) {
      setEmojiPickerKey(null)
      setEmojiPickerPos(null)
      return
    }
    const trigger = emojiTriggerRefs.current.get(key)
    if (trigger) {
      const rect = trigger.getBoundingClientRect()
      setEmojiPickerPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left })
    }
    setEmojiPickerKey(key)
  }

  const insertEmojiIntoTextarea = (targetId: number, emoji: string) => {
    const ta = localTextareaRefs.current[targetId]
    if (!ta) return
    const start = ta.selectionStart ?? ta.value.length
    const end = ta.selectionEnd ?? ta.value.length
    const next = ta.value.slice(0, start) + emoji + ta.value.slice(end)
    setNativeTextareaValue(ta, next)
    const newPos = start + emoji.length
    ta.setSelectionRange(newPos, newPos)
    ta.focus()
  }

  const isLocationSupportedForX = useFlipperFlag('location_tagging_x')
  const isLocationSupportedForLinkedIn = useFlipperFlag('location_tagging_linkedin')
  const isLocationSupportedForFacebook = useFlipperFlag('location_tagging_facebook')
  const isLocationSupportedForInstagram = useFlipperFlag('location_tagging_instagram')
  const isTaggingSupportedForX = useFlipperFlag('user_tagging_x')
  const isTaggingSupportedForInstagram = useFlipperFlag('user_tagging_instagram')
  const isMentioningSupportedForX = useFlipperFlag('user_mentioning_x')
  const isMentioningSupportedForLinkedIn = useFlipperFlag('user_mentioning_linkedin')
  const isMentioningSupportedForFacebook = useFlipperFlag('user_mentioning_facebook')
  const isMentioningSupportedForInstagram = useFlipperFlag('user_mentioning_instagram')

  const isLocationEnabled = (provider: ExternalIntegrationEnum) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isLocationSupportedForX
      case ExternalIntegrationEnum.LinkedIn:
        return isLocationSupportedForLinkedIn
      case ExternalIntegrationEnum.Facebook:
        return isLocationSupportedForFacebook
      case ExternalIntegrationEnum.Instagram:
        return isLocationSupportedForInstagram
      default:
        return false
    }
  }

  const isTaggingEnabled = (provider: ExternalIntegrationEnum) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isTaggingSupportedForX
      case ExternalIntegrationEnum.Instagram:
        return isTaggingSupportedForInstagram
      default:
        return false
    }
  }

  const isMentioningEnabled = (provider: ExternalIntegrationEnum, accountType?: string) => {
    switch (provider) {
      case ExternalIntegrationEnum.X:
        return isMentioningSupportedForX
      case ExternalIntegrationEnum.LinkedIn:
        return isMentioningSupportedForLinkedIn && accountType === 'page'
      case ExternalIntegrationEnum.Facebook:
        return isMentioningSupportedForFacebook && accountType === 'page'
      case ExternalIntegrationEnum.Instagram:
        return isMentioningSupportedForInstagram
      default:
        return false
    }
  }

  // True when at least one tagging-related flag is on globally — used to decide
  // whether to surface "Location tagging not supported" hints for providers that
  // don't support the feature at all (e.g., Google Business Profile).
  const taggingFeatureLive =
    isLocationSupportedForX ||
    isLocationSupportedForLinkedIn ||
    isLocationSupportedForFacebook ||
    isLocationSupportedForInstagram ||
    isTaggingSupportedForX ||
    isTaggingSupportedForInstagram

  const isLocationSupportedForRow = (provider: ExternalIntegrationEnum, accountType?: string) => {
    if (!isLocationEnabled(provider)) return false
    if (!LOCATION_SUPPORTED_PROVIDERS.has(provider)) return false
    if (provider === ExternalIntegrationEnum.LinkedIn && accountType !== undefined && accountType !== 'page') {
      return false
    }
    return true
  }

  const isTaggingSupportedForRow = (provider: ExternalIntegrationEnum) => {
    if (!isTaggingEnabled(provider)) return false
    if (!TAGS_SUPPORTED_PROVIDERS.has(provider)) return false
    return true
  }

  // Returns a tooltip when the provider supports tagging in general but the
  // current post's media disallows it (e.g., Instagram carousels). Returning
  // a string means we render the Tag User button in disabled state with that
  // tooltip rather than hiding the control.
  const getTaggingMediaBlockTooltip = (provider: ExternalIntegrationEnum): string | null => {
    if (provider === ExternalIntegrationEnum.Instagram && isCarousel) return CAROUSEL_TAGGING_TOOLTIP
    return null
  }

  const getLocationMediaBlockTooltip = (provider: ExternalIntegrationEnum): string | null => {
    if (provider === ExternalIntegrationEnum.Facebook && isVideo) return VIDEO_LOCATION_TOOLTIP
    return null
  }

  return (
    <div className={Styles.dynamicContent}>
      <div className={Styles.dynamicGrid}>
        {rows.map((row) => {
          const isUpdating = !!updatingProviders[row.provider]
          const firstAccountType = row.accountCaptionItems[0]?.accountType
          const rowLocationSupported = isLocationSupportedForRow(row.provider, firstAccountType)
          const rowTagsSupported = isTaggingSupportedForRow(row.provider)
          const showColumnUnsupportedHint = taggingFeatureLive && !rowLocationSupported && !rowTagsSupported

          return (
            <div key={row.provider} className={Styles.channelColumn}>
              <div className={Styles.channelHeader}>
                <div className={Styles.channelTitleGroup}>
                  <div className={Styles.channelTitleRow}>
                    <PlatformIcon platform={row.provider} size={20} isBrand />
                    <span className={Styles.channelTitle}>{row.label}</span>
                  </div>
                </div>
                <div className={Styles.channelActions}>
                  {row.isConnected && (
                    <ToggleButton
                      name={`${row.provider}-caption-toggle`}
                      checked={row.isEnabled}
                      disabled={isSaving}
                      handleChange={() => onToggleProvider(row.provider, !row.isEnabled)}
                      size="sm-md"
                    />
                  )}
                </div>
              </div>
              {!row.isConnected && (
                <InitiateOauthTrigger provider={row.provider} popUp onInitiatedOauth={connect}>
                  <Button
                    className={Styles.connectAccountButton}
                    variant="secondary"
                    size="md"
                    frontIcon={UserProfileAdd2}
                  >
                    Connect Account
                  </Button>
                </InitiateOauthTrigger>
              )}
              <div className={Styles.captionInputs}>
                {row.accountCaptionItems.map((item) => {
                  const target = item.target
                  const captionValue = target ? getDynamicCaptionValue(target) : row.initialCaption
                  const { count, limit, isOverLimit } = getCharacterStats(row.provider, captionValue)
                  const isGenerating = target ? !!generatingTargets[target.id] : false
                  const useCompactTextarea = row.accountCaptionItems.length > 1
                  const dataTestId = target
                    ? `${row.provider}-caption-group-${target.id}`
                    : `${row.provider}-caption-group-placeholder`

                  const isEmojiOpen = emojiPickerKey === String(item.key)
                  const isCaptionEditable = item.isIntegrationActive !== false && !!target
                  const textareaClassName = cx(Styles.dynamicTextarea, Styles.captionFieldEmbedded, {
                    [Styles.accountTextarea]: useCompactTextarea,
                    [Styles.hiddenInput]: isGenerating || isUpdating,
                  })

                  return (
                    <div key={item.key} className={Styles.accountCaptionGroup} data-testid={dataTestId}>
                      <div className={cx(Styles.captionInput)}>
                        {row.hasAccountIcon && row.isConnected && (
                          <div className={Styles.accountCaptionHeader}>
                            <div className={Styles.accountCaptionIdentity}>
                              <Avatar size={20} label={getInitials(item.accountName)} url={item.accountAvatar} />
                              <Text variant="secondary" color="var(--dark-80)">
                                {item.accountName}
                              </Text>
                            </div>
                          </div>
                        )}
                        <div className={Styles.inputContainer}>
                          <div
                            className={cx(Styles.captionFieldShell, {
                              [Styles.captionFieldShellInvalid]: isOverLimit,
                            })}
                          >
                            {isCaptionEditable &&
                            target &&
                            isMentioningEnabled(row.provider, item.accountType) &&
                            MENTIONS_SUPPORTED_PROVIDERS.has(row.provider) ? (
                              <MentionableTextarea
                                name={`${row.provider}-caption-${target.id}`}
                                value={captionValue}
                                onValueChange={(newValue) => onChangeCaption(target.id, newValue)}
                                integrationId={target.external_integration_id}
                                provider={row.provider}
                                isValid={!isOverLimit}
                                rows={useCompactTextarea ? 5 : 10}
                                maxHeight={useCompactTextarea ? 200 : 345}
                                disabled={isSaving}
                                inputRef={(element) =>
                                  handleTextareaRef(target.id, element as HTMLTextAreaElement | null)
                                }
                                className={textareaClassName}
                              />
                            ) : (
                              <Input
                                name={target ? `${row.provider}-caption-${target.id}` : `${row.provider}-caption`}
                                value={captionValue}
                                isTextarea
                                isValid={!isOverLimit}
                                rows={useCompactTextarea ? 5 : 10}
                                maxHeight={useCompactTextarea ? 200 : 345}
                                disabled={isSaving || !isCaptionEditable}
                                onChange={(event) => {
                                  if (!target) return
                                  onChangeCaption(target.id, event.target.value)
                                }}
                                ref={(element) => {
                                  if (!target) return
                                  handleTextareaRef(target.id, element as HTMLTextAreaElement | null)
                                }}
                                className={textareaClassName}
                              />
                            )}
                            <div className={Styles.captionToolbar}>
                              <span
                                ref={(el) => {
                                  if (el) emojiTriggerRefs.current.set(String(item.key), el)
                                  else emojiTriggerRefs.current.delete(String(item.key))
                                }}
                              >
                                <IconButton
                                  icon={SmileyHappyPlus}
                                  variant="ghost-tertiary"
                                  size="sm"
                                  title="Add emoji"
                                  onPress={() => toggleEmojiPicker(String(item.key))}
                                  isDisabled={!isCaptionEditable || isSaving}
                                />
                              </span>
                              <IconButton
                                icon={ArrowRefresh2}
                                variant="ghost-tertiary"
                                size="sm"
                                title={`Regenerate ${item.accountName}`}
                                onPress={() => target && onRegenerate(row.provider, target.id)}
                                isDisabled={!isCaptionEditable || isGenerating || isSaving}
                              />
                            </div>
                          </div>
                          {isEmojiOpen &&
                            target &&
                            emojiPickerPos &&
                            createPortal(
                              <div
                                className={Styles.emojiPickerOverlay}
                                style={{ bottom: emojiPickerPos.bottom, left: emojiPickerPos.left }}
                              >
                                <EmojiPicker
                                  onSelectEmoji={(emoji: BaseEmoji) => {
                                    insertEmojiIntoTextarea(target.id, emoji.native)
                                    setEmojiPickerKey(null)
                                    setEmojiPickerPos(null)
                                  }}
                                  onCancel={() => {
                                    setEmojiPickerKey(null)
                                    setEmojiPickerPos(null)
                                  }}
                                />
                              </div>,
                              document.body,
                            )}
                          {(isGenerating || isUpdating) && (
                            <div className={Styles.skeletonOverlay}>
                              <LineSkeleton className={Styles.skeletonLine} width="85%" />
                              <LineSkeleton className={Styles.skeletonLine} width="75%" />
                              <LineSkeleton className={Styles.skeletonLine} width="90%" />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={Styles.captionFooter}>
                        {row.isConnected && !row.isEnabled ? (
                          <div className={Styles.toggledOffHint}>
                            <AlertTriangle size={16} aria-hidden />
                            <span>Toggled off — won&apos;t post</span>
                          </div>
                        ) : (
                          <span />
                        )}
                        <div className={cx(Styles.maxCharacters, { [Styles.overLimit]: isOverLimit })}>
                          {count}/{limit}
                        </div>
                      </div>
                      {(() => {
                        const locationSupported = isLocationSupportedForRow(row.provider, item.accountType)
                        const tagsSupported = isTaggingSupportedForRow(row.provider)
                        const hasActiveTarget = isCaptionEditable
                        const isToggledOffOnly = row.isConnected && !row.isEnabled
                        const locationTooltip = isToggledOffOnly ? ENABLE_LOCATION_TOOLTIP : CONNECT_LOCATION_TOOLTIP
                        const taggingTooltip = isToggledOffOnly ? ENABLE_TAGGING_TOOLTIP : CONNECT_TAGGING_TOOLTIP
                        const hasTaggingContent = locationSupported || tagsSupported

                        if (!hasTaggingContent) return null

                        return (
                          <div className={Styles.taggingFooter}>
                            {locationSupported &&
                              (() => {
                                const mediaBlockTooltip = getLocationMediaBlockTooltip(row.provider)
                                if (mediaBlockTooltip) {
                                  return (
                                    <DisabledPickerTrigger
                                      icon={Marker2}
                                      label="Add Location"
                                      tooltipLabel={mediaBlockTooltip}
                                    />
                                  )
                                }
                                return hasActiveTarget && target ? (
                                  <LocationPicker
                                    integrationId={target.external_integration_id as number}
                                    provider={row.provider}
                                    location={locationsByTargetId[target.id] ?? null}
                                    onLocationChange={(location) => onChangeLocation(target.id, location)}
                                    disabled={isSaving}
                                  />
                                ) : (
                                  <DisabledPickerTrigger
                                    icon={Marker2}
                                    label="Add Location"
                                    tooltipLabel={locationTooltip}
                                  />
                                )
                              })()}
                            {tagsSupported &&
                              (() => {
                                const mediaBlockTooltip = getTaggingMediaBlockTooltip(row.provider)
                                if (mediaBlockTooltip) {
                                  return (
                                    <DisabledPickerTrigger
                                      icon={UserProfileAdd2}
                                      label="Tag User"
                                      tooltipLabel={mediaBlockTooltip}
                                    />
                                  )
                                }
                                return hasActiveTarget && target ? (
                                  <TagPicker
                                    integrationId={target.external_integration_id}
                                    tags={tagsByTargetId[target.id] ?? []}
                                    onTagsChange={(tags) => onChangeTags(target.id, tags)}
                                    disabled={isSaving}
                                    maxTags={MAX_TAGS_BY_PROVIDER[row.provider]}
                                  />
                                ) : (
                                  <DisabledPickerTrigger
                                    icon={UserProfileAdd2}
                                    label="Tag User"
                                    tooltipLabel={taggingTooltip}
                                  />
                                )
                              })()}
                          </div>
                        )
                      })()}
                    </div>
                  )
                })}
              </div>
              {showColumnUnsupportedHint && (
                <div className={Styles.unsupportedTaggingHint}>Location tagging not supported</div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
